import React, { useCallback, useEffect } from 'react';
import DashboardCard from 'components/DashboardCard/DashboardCard';
import { Add, LocationOn } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from '@tanstack/react-router';
import { DataGrid } from '@mui/x-data-grid';
import { actions } from '../ClientView.redux';
import ClientAddressesEditModal from './ClientAddressesEditModal';
import { Button } from '@mui/material';
import ClientAddressesCell from './ClientAddressesCell';
import NumberedDataGridPagination from 'components/NumberedDataGridPagination';

const ClientAddresses = () => {
    const dispatch = useDispatch();
    const { client_id } = useParams({ from: '/clients/view/$client_id' });
    const hasLoaded = useSelector((state) => state.clientView.addressesPreviouslyLoaded);
    const loading = useSelector((state) => state.clientView.addressesLoading);
    const addresses = useSelector((state) => state.clientView.addresses);
    const loadAddresses = (clientID) => dispatch(actions.loadAddresses(clientID));

    const columns = [
        {
            field: 'address_edit',
            headerName: 'Edit',
            width: 90,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => <ClientAddressesCell {...params} />,
        },
        { field: 'address_name', headerName: 'Name', flex: 1, minWidth: 150, sortable: false },
        { field: 'address_line_1', headerName: 'Line 1', flex: 1, minWidth: 160, sortable: false },
        { field: 'address_line_2', headerName: 'Line 2', flex: 1, minWidth: 160, sortable: false },
        { field: 'address_city', headerName: 'City', flex: 1, minWidth: 140, sortable: false },
        { field: 'address_state', headerName: 'State/Province', flex: 1, minWidth: 150, sortable: false },
        { field: 'address_postal_code', headerName: 'Postal Code', flex: 1, minWidth: 120, sortable: false },
        { field: 'address_country', headerName: 'Country', flex: 1, minWidth: 140, sortable: false },
        { field: 'date_created', headerName: 'Date Added', flex: 1, minWidth: 150, sortable: false },
    ];

    const handleAddNew = useCallback(() => {
        dispatch(actions.openAddressEditModal(null));
    }, [dispatch]);

    useEffect(() => {
        if (!hasLoaded && !loading) {
            loadAddresses(client_id);
        }
    }, [loadAddresses, client_id, hasLoaded, loading]);

    return (
        <DashboardCard
            color={'indigo'}
            icon={<LocationOn />}
            topContent={'Addresses'}
            topLabel={
                <div>
                    <Button variant="contained" color="primary" size="small" startIcon={<Add />} onClick={handleAddNew}>
                        Add New
                    </Button>
                </div>
            }
            size="xl"
        >
            <div style={{ width: '100%' }}>
                <DataGrid
                    rows={addresses}
                    columns={columns}
                    getRowId={(row) => row.address_id}
                    pagination
                    pageSizeOptions={[5, 10, 20, 50]}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 },
                        },
                    }}
                    loading={loading}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    disableCellFocus
                    sx={{
                        '& .MuiDataGrid-row': { cursor: 'pointer' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                            outline: 'none',
                        },
                    }}
                    slots={{ pagination: NumberedDataGridPagination }}
                    autoHeight
                />
            </div>

            <ClientAddressesEditModal />
        </DashboardCard>
    );
};

export default ClientAddresses;
